import * as React from 'react';

import { compose } from 'recompose';

import { get as getUser, isAuthenticatedUser } from 'js/lib/user';

import EnterpriseEnrollmentChoiceModalForXDP from 'bundles/enterprise-legacy-learner-home/components/EnterpriseEnrollmentChoiceModalForXDP';
import { PRODUCT_TYPES } from 'bundles/enterprise-legacy-learner-home/constants/ProgramActionConstants';
import { createContainer } from 'bundles/naptimejs';
import TopLevelModal from 'bundles/phoenix/components/TopLevelModal';
import EnterpriseDisplayEnrollButton from 'bundles/unified-description-page-common/components/enroll/enterprise/EnterpriseDisplayEnrollButton';
import type { EnrollButtonSize } from 'bundles/unified-description-page-common/components/enroll/enterprise/enterpriseEnrollButtonOverrideStyles';
import { useAvailableEnrollmentOptions } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseAvailableEnrollmentProgramsContext';
import { useEnterpriseCoursePageData } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseBffCourseContext';
import { useEnterpriseSpecializationPageData } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseBffSpecializationContext';

type PropsFromCaller = {
  enrollButtonRef?: React.MutableRefObject<HTMLDivElement | null>;
  shouldShowSponsoredByMessage?: boolean;
  size?: EnrollButtonSize;
  courseId?: string;
  privateSessionStart?: number;
  trackingOnClick?: () => void;
};

type Props = PropsFromCaller;

export const EnterpriseMultiProgramEnrollButtonNotConfigurable = ({
  enrollButtonRef,
  size,
  courseId,
  privateSessionStart,
  shouldShowSponsoredByMessage,
  trackingOnClick,
}: Props) => {
  const [showModal, setShowModal] = React.useState(false);

  const coursePageData = useEnterpriseCoursePageData();
  const specializationPageData = useEnterpriseSpecializationPageData();
  const availableEnrollmentOptions = useAvailableEnrollmentOptions();

  const invitedPrograms = React.useMemo(
    () => availableEnrollmentOptions?.invitedPrograms || [],
    [availableEnrollmentOptions]
  );
  const productId = coursePageData?.id ? coursePageData.id : (specializationPageData?.id as string);
  const productType = coursePageData?.id ? PRODUCT_TYPES.COURSE : PRODUCT_TYPES.SPECIALIZATION;
  const courseSlug = coursePageData?.slug || specializationPageData?.courses?.[0]?.slug;
  const userId = getUser().id;

  const onClickEnroll = () => {
    if (trackingOnClick) {
      trackingOnClick();
    }

    setShowModal(true);
  };

  if ((!coursePageData?.id && !specializationPageData?.id) || !isAuthenticatedUser()) {
    return null;
  }

  return (
    <React.Fragment>
      <EnterpriseDisplayEnrollButton
        courseId={courseId}
        privateSessionStart={privateSessionStart}
        size={size}
        shouldShowSponsoredByMessage={shouldShowSponsoredByMessage}
        onClick={onClickEnroll}
        enrollButtonRef={enrollButtonRef}
        trackingName="multi_program_enroll_button"
        dataE2eName="multi-program-enroll-button"
      />
      {showModal && (
        <TopLevelModal>
          <EnterpriseEnrollmentChoiceModalForXDP
            productType={productType}
            productId={productId}
            programs={availableEnrollmentOptions?.programs}
            invitedPrograms={invitedPrograms}
            thirdPartyOrganizations={availableEnrollmentOptions?.thirdPartyOrganizations}
            invitedThirdPartyOrganizations={availableEnrollmentOptions?.invitedThirdPartyOrganizations}
            handleClose={() => setShowModal(false)}
            userId={userId}
            waitForBothProgramDataFetch={false}
            courseSlug={courseSlug}
          />
        </TopLevelModal>
      )}
    </React.Fragment>
  );
};

export default compose<Props, PropsFromCaller>(createContainer(() => ({})))(
  EnterpriseMultiProgramEnrollButtonNotConfigurable
);
